'use client'

import { 
  Monitor, 
  Grid3x3, 
  Layers, 
  Wand2,
  Palette,
  Type,
  Image as ImageIcon,
  Users,
  FileJson,
  Zap,
  Share2
} from "lucide-react"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ScrollReveal } from "@/components/scroll-reveal"

export function DetailedFeatures() {
  const { t } = useLanguage()

  const freeFeatures = [
    {
      icon: Monitor,
      title: t('landing.detailed.patterns30'),
      description: t('landing.detailed.patterns30.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: Palette,
      title: t('landing.detailed.ledWall'),
      description: t('landing.detailed.ledWall.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: Wand2,
      title: t('landing.detailed.selfTest'),
      description: t('landing.detailed.selfTest.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: Type,
      title: t('landing.detailed.texts'),
      description: t('landing.detailed.texts.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: ImageIcon,
      title: t('landing.detailed.logos'),
      description: t('landing.detailed.logos.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: Zap,
      title: t('landing.detailed.externalDisplay'),
      description: t('landing.detailed.externalDisplay.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    },
    {
      icon: FileJson,
      title: t('landing.detailed.configs'),
      description: t('landing.detailed.configs.desc'),
      badge: t('landing.detailed.free'),
      badgeColor: "bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20"
    }
  ]

  const proFeatures = [
    {
      icon: Layers,
      title: t('landing.detailed.multigrid'),
      description: t('landing.detailed.multigrid.desc'),
      badge: t('landing.detailed.pro'),
      badgeColor: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
    },
    {
      icon: Grid3x3,
      title: t('landing.detailed.synoptic'),
      description: t('landing.detailed.synoptic.desc'),
      badge: t('landing.detailed.pro'),
      badgeColor: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
    },
    {
      icon: Share2,
      title: t('landing.detailed.sharedSessions'),
      description: t('landing.detailed.sharedSessions.desc'),
      badge: t('landing.detailed.pro'),
      badgeColor: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
    },
    {
      icon: Users,
      title: t('landing.detailed.support'),
      description: t('landing.detailed.support.desc'),
      badge: t('landing.detailed.pro'),
      badgeColor: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
    }
  ]

  return (
    <section className="py-16 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="w-full relative z-10 px-4 md:px-6 flex flex-col items-center">
        <ScrollReveal animation="bounce-in" duration={1}>
          <div className="text-center mb-12 md:mb-20 max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-6">
              {t('landing.detailed.title')}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed px-4">
              {t('landing.detailed.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        {/* Free Features */}
        <div className="w-full max-w-7xl mb-16">
          <ScrollReveal animation="slide-right" duration={0.8}>
            <h3 className="text-2xl md:text-4xl font-bold mb-8 text-center">
              {t('landing.detailed.freeTitle')}
            </h3>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {freeFeatures.map((feature, index) => (
              <ScrollReveal
                key={index}
                animation="scale-in"
                delay={index * 0.05}
                duration={0.6}
                className="group relative"
              >
                <div className="absolute -inset-1 bg-foreground/5 opacity-0 group-hover:opacity-100 transition" />
                <div className="relative border-2 border-foreground/10 p-4 md:p-6 bg-background hover:border-foreground/30 transition-all h-full flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <feature.icon className="w-6 h-6" />
                      <span className={`px-2 py-0.5 border text-xs font-semibold ${feature.badgeColor}`}>
                        {feature.badge}
                      </span>
                    </div>
                    <h4 className="text-base md:text-lg font-bold mb-2">{feature.title}</h4>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mt-auto">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>

        {/* Pro Features */}
        <div className="w-full max-w-7xl">
          <ScrollReveal animation="slide-left" duration={0.8}>
            <h3 className="text-2xl md:text-4xl font-bold mb-8 text-center">
              {t('landing.detailed.proTitle')}
            </h3>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {proFeatures.map((feature, index) => (
              <ScrollReveal
                key={index}
                animation="scale-in"
                delay={index * 0.1}
                duration={0.6}
                className="group relative"
              >
                <div className="absolute -inset-1 bg-amber-500/10 opacity-0 group-hover:opacity-100 transition" />
                <div className="relative border-2 border-amber-500/20 p-4 md:p-6 bg-background hover:border-amber-500/40 transition-all h-full flex flex-col">
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                      <feature.icon className="w-6 h-6" />
                      <span className={`px-2 py-0.5 border text-xs font-semibold ${feature.badgeColor}`}>
                        {feature.badge}
                      </span>
                    </div>
                    <h4 className="text-base md:text-lg font-bold mb-2">{feature.title}</h4>
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground leading-relaxed mt-auto">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>

        {/* Stats */}
        <ScrollReveal animation="fade-in" delay={0.4} duration={1} className="mt-16 md:mt-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-4xl mx-auto">
            {[
              { value: "30+", label: t('landing.detailed.stats.patterns') },
              { value: "16K", label: t('landing.detailed.stats.resolution') },
              { value: "Windows & macOS", label: t('landing.detailed.stats.download') },
              { value: "9.99€", label: t('landing.detailed.stats.proMonth') }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className={`${i === 2 ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-bold mb-2`}>{stat.value}</div>
                <div className="text-xs md:text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
